import { Avatar, Col, face, Icon, SectionLabel, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { callCard } from "../../call/resource.tsx";
import { say } from "../../localization/say.ts";
import { faceOfAnAgent } from "../../me/an-agents-mark.ts";
import { calledBy } from "../../me/called.ts";
import { useFace } from "../../me/face.ts";
import { read } from "../../net/rally.ts";
import { usePresent } from "../../push/here.ts";
import { coalesced } from "../activity-refresh.ts";
import { fileCard } from "../attachment/file-resource.tsx";
import { Files } from "../attachment/files.tsx";
import { AddSomebody } from "../person/add-somebody.tsx";
import { type PersonHere, personCard } from "../person/person.tsx";
import { agentCard } from "../run/agent.tsx";
import { useLive } from "../run/live.ts";
import { useNow } from "../run/tool-frame.tsx";
import { elapsedSaid, nameOf, type Someone, stateOf, turnOf } from "../run/working.ts";
import { useStack } from "./panels.tsx";
import type { Card } from "./stacking.ts";

type Agent = { account: string; handle: string; model: string; owner: string };

type Busy = { thinking: Someone[]; typing: Someone[]; unanswered: Someone[] };

export function detailsCard(conversation: string, me: string): Card {
  return {
    key: `details:${conversation}`,
    title: say("panel.details"),
    render: () => <Details conversation={conversation} me={me} />,
  };
}

const A_PERSON_IS = face.row;
const A_FEW_AGENTS = 6;

const A_WORKER_IS = face.small;

function PersonRow({
  who,
  says,
  clock,
  live,
  under,
  onPress,
}: {
  who: PersonHere;

  says: string | null;

  clock?: string | null;

  live?: boolean;

  under?: boolean | undefined;
  onPress: () => void;
}) {
  const theme = useTheme();
  const face = useFace(who.avatar);
  const name = calledBy(who) ?? who.handle;
  return (
    <Tappable
      accessibilityRole="button"
      testID={`details-person-${who.account}`}
      onPress={onPress}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: under ? theme.space[2] : theme.space[3],
        paddingVertical: under ? theme.space[1] : theme.space[2],
        paddingHorizontal: theme.space[2],
        marginLeft: under ? theme.space[4] : 0,
        borderRadius: theme.radius.md,
      }}
    >
      {under ? (
        <Col
          style={{
            width: 1,
            alignSelf: "stretch",
            marginRight: theme.space[1],
            backgroundColor: theme.color["border.default"],
          }}
        />
      ) : null}
      <Avatar
        name={name}
        src={face}
        size={under ? A_WORKER_IS : A_PERSON_IS}
        present={usePresent(who.account)}
        {...(who.agent ? faceOfAnAgent(who.model) : {})}
      />
      <Col style={{ flex: 1, minWidth: 0 }}>
        <Text
          typeName={under ? "caption" : "body"}
          color={under ? "text.secondary" : "text.primary"}
          numberOfLines={1}
        >
          {name}
        </Text>
      </Col>
      {live ? (
        <Col style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: theme.color.accent }} />
      ) : null}
      {says ? (
        <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
          {says}
        </Text>
      ) : null}
      {clock ? (
        <Text typeName="caption" color="text.tertiary" testID={`details-elapsed-${who.account}`}>
          {clock}
        </Text>
      ) : null}
    </Tappable>
  );
}

function roleSaid(role: string): string | null {
  if (role === "owner") return say("person.owner");
  if (role === "admin") return say("person.admin");
  return null;
}

function stateSaid(account: string, busy: Busy | null): string | null {
  const state = stateOf(account, busy);
  if (state === "thinking") return say("agents.thinkingNow");
  if (state === "waiting") return say("agents.owed");
  return null;
}

const A_BEAT = 3000;

export function Details({ conversation, me }: { conversation: string; me: string }) {
  const theme = useTheme();
  const stack = useStack();
  const [people, setPeople] = useState<PersonHere[] | null>(null);
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [busy, setBusy] = useState<Busy | null>(null);
  const [adding, setAdding] = useState(false);
  const [allAgents, setAllAgents] = useState(false);

  const again = useCallback(() => {
    void read<PersonHere[]>("conversation.people", { conversation })
      .then(setPeople)
      .catch(() => setPeople([]));

    void read<Agent[]>("my.agents")
      .then(setAgents)
      .catch(() => setAgents([]));
  }, [conversation]);
  useEffect(again, [again]);

  useEffect(() => {
    const ask = () => {
      void read<Busy>("conversation.busy", { conversation })
        .then(setBusy)
        .catch(() => setBusy(null));
    };
    ask();
    const timer = setInterval(ask, A_BEAT);
    return () => clearInterval(timer);
  }, [conversation]);

  const refresh = useMemo(() => coalesced(again), [again]);
  useEffect(() => () => refresh.cancel(), [refresh]);
  useLive((notice) => {
    if (notice.conversation === conversation) refresh.notice();
  });

  const now = useNow((busy?.thinking.length ?? 0) > 0);
  const known = new Set((agents ?? []).map((one) => one.account));
  const here = people ?? [];
  const persons = here.filter((one) => !known.has(one.account));
  const agentsHere = here.filter((one) => known.has(one.account));

  const mayAdd = here.some((one) => one.account === me && (one.role === "owner" || one.role === "admin"));

  return (
    <Col style={{ gap: theme.space[4] }}>
      <Col>
        <SectionLabel title={say("panel.people")} count={persons.length} />
        {persons.map((who) => (
          <PersonRow
            key={who.account}
            who={who}
            says={roleSaid(who.role)}
            onPress={() => stack.open(personCard(conversation, me, who))}
          />
        ))}
      </Col>

      <Col testID="details-agents">
        <SectionLabel
          title={say("panel.agents")}
          count={agentsHere.length}
          action={
            mayAdd ? (
              <Tappable
                accessibilityRole="button"
                accessibilityLabel={say("head.addSomebody")}
                testID="details-add-agent"
                onPress={() => setAdding(true)}
                style={{
                  width: 24,
                  height: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: theme.radius.md,
                }}
              >
                <Icon name="plus" size={14} color="text.secondary" />
              </Tappable>
            ) : null
          }
        />
        {(allAgents ? agentsHere : agentsHere.slice(0, A_FEW_AGENTS)).map((who) => (
          <PersonRow
            key={who.account}
            who={who}
            says={stateSaid(who.account, busy)}
            live={stateOf(who.account, busy) === "thinking"}
            clock={elapsedSaid(turnOf(who.account, busy)?.startedAt, now)}
            onPress={() => stack.open(agentCard(conversation, me, who))}
          />
        ))}
        {agentsHere.length > A_FEW_AGENTS ? (
          <Tappable
            accessibilityRole="button"
            testID="details-more-agents"
            onPress={() => setAllAgents((was) => !was)}
            style={{ paddingVertical: theme.space[2], paddingHorizontal: theme.space[2] }}
          >
            <Text typeName="caption" color="accent">
              {allAgents
                ? say("panel.fewerAgents")
                : say("panel.moreAgents")(agentsHere.length - A_FEW_AGENTS)}
            </Text>
          </Tappable>
        ) : null}
      </Col>

      <Files
        conversation={conversation}
        onOpen={(file) => stack.open(fileCard(file, conversation))}
        onOpenCall={(call) => stack.open(callCard(call, conversation, me))}
      />

      <AddSomebody
        conversation={conversation}
        visible={adding}
        here={here.map((one) => one.account)}
        me={me}
        mayAdd={mayAdd}
        onDismiss={() => setAdding(false)}
        onAdded={() => again()}
      />
    </Col>
  );
}

export const rowWould = { roleSaid, stateSaid, nameOf };
